// Editorul de poză: alegi ce anume din imagine intră în cadru.
//
// Până acum o poză încărcată era tăiată automat din mijloc. Pentru un avatar asta e
// aproape întotdeauna greşit — oamenii nu stau în centrul geometric al fotografiilor
// lor. Aici cadrul rămâne fix şi se mişcă imaginea sub el, ceea ce e şi mai uşor de
// înţeles: vezi tot timpul exact rezultatul, nu o previzualizare a lui.
//
// Pânza **este** cadrul, iar rotunjirea o face CSS-ul. Aşa nu desenăm măşti, umbre şi
// găuri — cercul din ecran e acelaşi dreptunghi tăiat, doar cu colţurile rotunjite.
//
// Ce iese de aici e deja mic: tăiem la dimensiunea afişată şi coborâm calitatea în
// trepte până intrăm sub prag. Nimic din fotografia originală de trei megaocteţi nu
// ajunge pe server.

import { el } from '../core/util.js';
import { icon } from './icons.js';
import { modal, toast } from './components.js';

/** Cât de mult se poate mări. Peste patru, orice fotografie de telefon se face pastă. */
const ZOOM_MAX = 4;

/**
 * Deschide editorul şi întoarce imaginea tăiată, comprimată, ca `data:` URL.
 *
 * Întoarce `null` dacă omul a închis fereastra fără să confirme.
 *
 * @param {File}   file    poza aleasă de pe dispozitiv
 * @param {object} optiuni forma cadrului, dimensiunea de ieşire şi ţinta în octeţi
 */
export async function editorImagine(file, {
  forma = 'cerc',           // „cerc" pentru avatar, „banda" pentru copertă
  latimeIesire = 512,
  raport = 1,               // lăţime / înălţime
  tinta = 40 * 1024,
  titlu = 'Кадрирование',
} = {}) {
  const img = await incarcaImagine(file);

  // Cadrul de pe ecran. Coperta e lată şi joasă, avatarul e pătrat.
  const latimeCadru = forma === 'banda' ? 460 : 260;
  const inaltimeCadru = Math.round(latimeCadru / raport);

  const panza = el('canvas', { class: `ie-panza ${forma}`, width: latimeCadru,
    height: inaltimeCadru });
  const ctx = panza.getContext('2d');

  // Cât trebuie mărită imaginea ca să acopere cadrul. De aici pornim: un cadru cu
  // margini goale n-are cum să fie rezultatul dorit, deci nici măcar nu-l permitem.
  const acoperire = Math.max(latimeCadru / img.width, inaltimeCadru / img.height);
  let zoom = 1;
  let ox = 0;
  let oy = 0;

  const limiteaza = () => {
    const k = acoperire * zoom;
    ox = Math.min(0, Math.max(latimeCadru - img.width * k, ox));
    oy = Math.min(0, Math.max(inaltimeCadru - img.height * k, oy));
  };

  const deseneaza = () => {
    limiteaza();
    const k = acoperire * zoom;
    ctx.clearRect(0, 0, latimeCadru, inaltimeCadru);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, ox, oy, img.width * k, img.height * k);
  };

  // Poziţionare iniţială: mijlocul imaginii în mijlocul cadrului.
  ox = (latimeCadru - img.width * acoperire) / 2;
  oy = (inaltimeCadru - img.height * acoperire) / 2;
  deseneaza();

  // --- mutarea cu degetul sau cu mouse-ul ---
  let apucat = null;
  const laApasare = (e) => {
    apucat = { x: (e.touches?.[0] ?? e).clientX - ox, y: (e.touches?.[0] ?? e).clientY - oy };
    e.preventDefault();
  };
  const laMiscare = (e) => {
    if (!apucat) return;
    const p = e.touches?.[0] ?? e;
    ox = p.clientX - apucat.x;
    oy = p.clientY - apucat.y;
    deseneaza();
    e.preventDefault();
  };
  const laRidicare = () => { apucat = null; };

  panza.addEventListener('mousedown', laApasare);
  panza.addEventListener('touchstart', laApasare, { passive: false });
  window.addEventListener('mousemove', laMiscare);
  window.addEventListener('touchmove', laMiscare, { passive: false });
  window.addEventListener('mouseup', laRidicare);
  window.addEventListener('touchend', laRidicare);

  // --- mărirea ---
  const cursor = el('input', { class: 'ie-cursor', type: 'range', min: '1',
    max: String(ZOOM_MAX), step: '0.01', value: '1' });

  /** Măreşte păstrând sub deget acelaşi punct din imagine. */
  const schimbaZoom = (nou, catreX = latimeCadru / 2, catreY = inaltimeCadru / 2) => {
    const vechi = zoom;
    zoom = Math.min(ZOOM_MAX, Math.max(1, nou));
    const raportZoom = zoom / vechi;
    ox = catreX - (catreX - ox) * raportZoom;
    oy = catreY - (catreY - oy) * raportZoom;
    cursor.value = String(zoom);
    deseneaza();
  };

  cursor.oninput = () => schimbaZoom(parseFloat(cursor.value));
  panza.addEventListener('wheel', (e) => {
    e.preventDefault();
    const r = panza.getBoundingClientRect();
    schimbaZoom(zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX - r.left, e.clientY - r.top);
  }, { passive: false });

  const curata = () => {
    window.removeEventListener('mousemove', laMiscare);
    window.removeEventListener('touchmove', laMiscare);
    window.removeEventListener('mouseup', laRidicare);
    window.removeEventListener('touchend', laRidicare);
  };

  return new Promise((resolve) => {
    let raspuns = null;
    modal({
      title: titlu,
      width: forma === 'banda' ? 540 : 360,
      onClose: () => { curata(); resolve(raspuns); },
      body: el('div', { class: 'ie-box' }, [
        el('div', { class: `ie-rama ${forma}` }, [panza]),
        el('div', { class: 'ie-unelte' }, [
          icon('search', 12),
          cursor,
        ]),
        el('p', { class: 'set-hint',
          text: 'Потяните картинку, чтобы сдвинуть. Колесо или ползунок — масштаб.' }),
      ]),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        {
          label: 'Готово', primary: true,
          onClick: (c) => {
            raspuns = taieSiComprima(img, {
              acoperire, zoom, ox, oy, latimeCadru, inaltimeCadru, latimeIesire, raport, tinta,
            });
            c();
          },
        },
      ],
    });
  });
}

/**
 * Redesenează la dimensiunea finală şi coboară calitatea până intră sub ţintă.
 *
 * Aceeaşi scară de trepte ca la imaginile din publicaţii: ochiul iartă mai uşor o
 * compresie mai dură decât o poză vizibil mai mică, deci întâi scade calitatea şi abia
 * la urmă dimensiunea.
 */
function taieSiComprima(img, o) {
  let latime = o.latimeIesire;

  for (let pas = 0; pas < 4; pas++) {
    const inaltime = Math.round(latime / o.raport);
    const panza = document.createElement('canvas');
    panza.width = latime;
    panza.height = inaltime;
    const ctx = panza.getContext('2d');
    ctx.imageSmoothingQuality = 'high';

    // Tot ce s-a văzut pe ecran se scalează cu acelaşi factor: cadrul de previzualizare
    // şi cel final sunt aceeaşi imagine la două mărimi.
    const f = latime / o.latimeCadru;
    const k = o.acoperire * o.zoom * f;
    ctx.drawImage(img, o.ox * f, o.oy * f, img.width * k, img.height * k);

    // Pornim de la o calitate mare si coboram doar cat trebuie. Cu bugetele de acum
    // (110 KB la avatar, 420 la coperta) primul pas incape aproape mereu, deci poza
    // ajunge asa cum a iesit din aparat, nu strivita din primul pas.
    for (const calitate of [0.94, 0.88, 0.8, 0.7, 0.58]) {
      const url = panza.toDataURL('image/webp', calitate);
      if (marimeDataUrl(url) <= o.tinta) return url;
    }
    latime = Math.round(latime * 0.75);
  }
  // Nici la calitate minimă şi un sfert din laturi n-a încăput: dăm ultima variantă,
  // fiindcă o poză puţin peste prag e mai bună decât un buton care nu face nimic.
  return ultimaIncercare(img, o, latime);
}

function ultimaIncercare(img, o, latime) {
  const inaltime = Math.round(latime / o.raport);
  const panza = document.createElement('canvas');
  panza.width = latime;
  panza.height = inaltime;
  const ctx = panza.getContext('2d');
  const f = latime / o.latimeCadru;
  const k = o.acoperire * o.zoom * f;
  ctx.drawImage(img, o.ox * f, o.oy * f, img.width * k, img.height * k);
  return panza.toDataURL('image/webp', 0.5);
}

function marimeDataUrl(url) {
  const b64 = url.slice(url.indexOf(',') + 1);
  return Math.floor(b64.length * 3 / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
}

function incarcaImagine(file) {
  return new Promise((resolve, reject) => {
    if (!file.type?.startsWith('image/')) { reject(new Error('Это не картинка')); return; }
    const cititor = new FileReader();
    cititor.onerror = () => reject(new Error('Не удалось прочитать файл'));
    cititor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Не удалось открыть картинку'));
      img.onload = () => resolve(img);
      img.src = cititor.result;
    };
    cititor.readAsDataURL(file);
  });
}

/**
 * Deschide selectorul de fişiere, trece poza prin editor şi o predă mai departe.
 *
 * Un singur loc pentru tot drumul „aleg poza → o încadrez → o trimit", fiindcă avatarul
 * şi coperta îl fac identic şi orice diferenţă între ele ar fi o scăpare, nu o intenţie.
 */
export function alegeSiEditeaza(optiuni, gata) {
  const camp = el('input', { type: 'file', accept: 'image/*', style: 'display:none' });
  camp.onchange = async () => {
    const f = camp.files?.[0];
    camp.value = '';
    if (!f) return;
    try {
      const url = await editorImagine(f, optiuni);
      // `null` înseamnă că omul a închis fereastra fără să confirme — nimic de făcut.
      if (url) gata(url);
    } catch (err) { toast(err.message, 'error'); }
  };
  document.body.append(camp);
  camp.click();
  setTimeout(() => camp.remove(), 60_000);
}
